import {
    Box, Button, Typography, CircularProgress,
    DialogTitle,
    DialogContent,
    DialogActions,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Tooltip,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { isAuthorizedQuery } from 'src/selectors/isAuthorizedQuery';
import { useQueryProductVersion } from 'src/hooks/useQueryProductVersion';
import { useQueryConsolidationConfiguration } from 'src/hooks/useQueryConsolidationConfiguration';
import { CONSOLIDATABLE_ATTRIBUTES, OptionGroupType } from 'src/constants';
import {
    Check, Close, ExpandMore,
    Info,
} from '@mui/icons-material';
import { config } from 'src/lib/config';
import { OptionsTable } from './OptionsTable';

interface OptionGroupDetailsProps {
    groupType: OptionGroupType;
    groupId: string | null;
    isEditingExistingGroup: boolean;
    onDone: () => void;
    productKey: string;
    currentGroupSpec: Models.IngestConfigApi.ForcedRankings.PartialProductSpec | null;
    version?: number;
    addOptionGroup: (
        groupType: OptionGroupType,
        id: string,
        spec: Models.IngestConfigApi.ForcedRankings.PartialProductSpec
    ) => void;
    updateOptionGroup?: (
        groupType: OptionGroupType,
        id: string,
        update: Models.IngestConfigApi.ForcedRankings.PartialProductSpec
    ) => void;
}

export const OptionGroupDetails = ({
    groupType,
    groupId: _groupId,
    isEditingExistingGroup,
    onDone,
    productKey,
    version,
    addOptionGroup,
    updateOptionGroup,
    currentGroupSpec,
}: OptionGroupDetailsProps): JSX.Element => {
    const dccConfigLink = `${config.ingestToolRootUrl}/product/${productKey}/consolidation`;
    const accessToken = useRecoilValue(isAuthorizedQuery);
    const { data: productData, isLoading: isProductLoading } = useQueryProductVersion(
        accessToken,
        { productKey, version },
        { enabled: !!productKey },
    );
    const { data: consolidationConfig, isLoading: isConsolidationLoading } = useQueryConsolidationConfiguration(
        accessToken,
        productKey,
        { enabled: !!productKey },
    );

    const groupOptions = useMemo(() => {
        const options = productData?.result?.[0]?.options || [];
        const map: Record<string, string[]> = {};

        options.forEach((opt: any) => {
            map[opt.name] = opt.values ? [...opt.values].sort() : [];
        });

        CONSOLIDATABLE_ATTRIBUTES.forEach((attr) => {
            if (!map[attr.value]) {
                map[attr.value] = attr.options;
            }
        });

        return map;
    }, [productData]);

    const [values, setValues] = useState<Record<string, string>>(currentGroupSpec?.values ?? {});

    const allOptionNames = useMemo(() => {
        const names = new Set<string>([...Object.keys(groupOptions), ...Object.keys(values)]);

        return Array.from(names).sort((a, b) => a.localeCompare(b));
    }, [groupOptions, values]);

    const legacyOptionNames = useMemo(() => Object.keys(values)
        .filter((name) => !groupOptions[name] && values[name]), [values, groupOptions]);

    const expandedOptionNames = useMemo(
        () => allOptionNames.filter((name) => (consolidationConfig?.productOptionsToExpand?.includes(name)
            || consolidationConfig?.attributesToExpand?.includes(name))
                && !legacyOptionNames.includes(name)),
        [allOptionNames, consolidationConfig, legacyOptionNames],
    );

    const consolidatedOptionNames = useMemo(
        () => allOptionNames.filter((name) => !consolidationConfig?.productOptionsToExpand?.includes(name)
            && !consolidationConfig?.attributesToExpand?.includes(name)
            && !legacyOptionNames.includes(name)),
        [allOptionNames, consolidationConfig, legacyOptionNames],
    );

    const isLoading = isProductLoading || isConsolidationLoading;

    const handleValueChange = (optionName: string, value: string): void => {
        setValues((prev) => ({ ...prev, [optionName]: value }));
    };

    const handleAssign = (): void => {
        const id = _groupId || crypto.randomUUID();
        const assignValues = Object.fromEntries(
            Object.entries(values).filter(([_, v]) => v !== ''),
        );
        const spec = {
            name: Object.values(assignValues).join(', '),
            values: assignValues,
        };

        if (!isEditingExistingGroup) {
            addOptionGroup(groupType, id, spec);
        } else if (updateOptionGroup) {
            updateOptionGroup(groupType, id, spec);
        }
        onDone();
    };

    // Check if any expanded options are missing values for filtering groups
    const hasMissingExpandedValues = useMemo(() => {
        if (groupType !== OptionGroupType.FILTERING) {
            return false;
        }

        return expandedOptionNames.some((name) => !values[name]);
    }, [groupType, expandedOptionNames, values]);

    const shouldDisableAssign = isLoading
        || Object.values(values).length === 0
        || hasMissingExpandedValues;

    const primaryOptions = groupType === OptionGroupType.FILTERING ? expandedOptionNames : consolidatedOptionNames;
    const secondaryOptions = groupType === OptionGroupType.FILTERING ? consolidatedOptionNames : expandedOptionNames;
    const primaryOptionsLabel = groupType === OptionGroupType.FILTERING ? 'Expanded Options' : 'Consolidated Options';
    const secondaryOptionsLabel = groupType === OptionGroupType.FILTERING ? 'Consolidated Options' : 'Expanded Options';

    return (
        <>
            <DialogTitle>
                {`${isEditingExistingGroup ? 'Edit' : 'Create'} ${groupType === OptionGroupType.FILTERING ? 'Filtering' : 'Default'} Option Group`}
            </DialogTitle>
            <DialogContent sx={{ height: 'calc(100% - 120px)', p: 6 }}>
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Typography variant="caption">
                            {groupType === OptionGroupType.FILTERING
                                ? (
                                    <Typography variant="caption">
                                        Filtering option groups specify the options which must exist
                                        for the design that is force ranked. All expanded options in the
                                        {' '}
                                        <a href={dccConfigLink} rel="noopener noreferrer" target="_blank">design concept consolidation configuration</a>
                                        {' '}
                                        must be assigned a value.
                                    </Typography>
                                )
                                : (
                                    <Typography variant="caption">
                                        Default option groups can be used as an extra layer of specificity
                                        to detemine which options are used for the ranking.
                                        Only the consolidated options in the
                                        {' '}
                                        <a href={dccConfigLink} rel="noopener noreferrer" target="_blank">design concept consolidation configuration</a>
                                        {' '}
                                        can be assigned a value.
                                    </Typography>
                                )}
                        </Typography>
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="body1">{primaryOptionsLabel}</Typography>
                            {primaryOptions.length === 0 ? (
                                <Typography sx={{ my: 2 }}>
                                    {`No ${primaryOptionsLabel.toLowerCase()} available`}
                                </Typography>
                            ) : (
                                <Box sx={{ mt: 2 }}>
                                    <OptionsTable
                                        groupOptions={groupOptions}
                                        optionNames={primaryOptions}
                                        values={values}
                                        onValueChange={handleValueChange}
                                    />
                                </Box>
                            )}
                        </Box>
                        <Box>
                            <Accordion sx={{ my: 2 }}>
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography>{secondaryOptionsLabel}</Typography>
                                        <Tooltip title={groupType === OptionGroupType.FILTERING
                                            ? `Consolidated options should not norammaly be assigned a value in filtering option groups. Use this section only when preparing to expand them in the design concept consolidation configuration. This should be done before expanding the option to prevent duplicate designs in the Gallery.`
                                            : `Expanded options should not be assigned a value in default option groups. They only exist here for the case where the design concept consolidation configuration is updated and the option groups are not updated.`}
                                        >
                                            <Info sx={{
                                                ml: 1, height: 16, width: 16, color: 'primary.main',
                                            }}
                                            />
                                        </Tooltip>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {secondaryOptions.length === 0 ? (
                                        <Typography>
                                            {`No ${secondaryOptionsLabel.toLowerCase()} available`}
                                        </Typography>
                                    ) : (
                                        <OptionsTable
                                            groupOptions={groupOptions}
                                            optionNames={secondaryOptions}
                                            values={values}
                                            onValueChange={handleValueChange}
                                        />
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        </Box>
                        {legacyOptionNames.length > 0 && (
                            <Box>
                                <Accordion sx={{ my: 2 }}>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography>Legacy Options</Typography>
                                            <Tooltip title="These options are no longer available in the current product version but are preserved to maintain compatibility with existing configurations.">
                                                <Info sx={{
                                                    ml: 1, height: 16, width: 16, color: 'primary.main',
                                                }}
                                                />
                                            </Tooltip>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <OptionsTable
                                            isLegacy
                                            groupOptions={groupOptions}
                                            optionNames={legacyOptionNames}
                                            values={values}
                                            onValueChange={handleValueChange}
                                        />
                                    </AccordionDetails>
                                </Accordion>
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 4 }}>
                <Button color="secondary" startIcon={<Close />} variant="outlined" onClick={onDone}>CANCEL</Button>
                <Button color="primary" disabled={shouldDisableAssign} startIcon={<Check />} variant="contained" onClick={handleAssign}>ASSIGN</Button>
            </DialogActions>
        </>
    );
};
